import { useState, useEffect, useCallback } from 'react'
import { initMetrika, clearMetrikaCookies } from '../lib/metrika'

/**
 * Согласие на аналитические cookie.
 *
 * Хранится в localStorage вместе с версией: если объём обработки
 * изменится (новый счётчик, включённый Вебвизор), версия поднимается
 * и согласие спрашивается заново — старое «Принять» его не покрывает.
 */

export const CONSENT_VERSION = 1
const STORAGE_KEY = 'cookie-consent'

export type ConsentChoice = 'granted' | 'denied'

interface StoredConsent {
  v: number
  analytics: boolean
  ts: string
}

function readStored(): ConsentChoice | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<StoredConsent>
    // Согласие другой версии считаем не данным — спросим заново
    if (parsed.v !== CONSENT_VERSION) return null
    if (typeof parsed.analytics !== 'boolean') return null

    return parsed.analytics ? 'granted' : 'denied'
  } catch {
    // Приватный режим, отключённое хранилище, битый JSON
    return null
  }
}

function writeStored(choice: ConsentChoice): void {
  try {
    const value: StoredConsent = {
      v: CONSENT_VERSION,
      analytics: choice === 'granted',
      ts: new Date().toISOString(),
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Не смогли сохранить — баннер спросит в следующий раз.
    // Счётчик при этом всё равно не запустится без согласия.
  }
}

export function useCookieConsent() {
  const [choice, setChoice] = useState<ConsentChoice | null>(readStored)
  const [isOpen, setIsOpen] = useState(() => readStored() === null)

  // Запускаем счётчик при загрузке, если согласие уже было дано ранее
  useEffect(() => {
    if (choice === 'granted') initMetrika()
  }, [choice])

  const accept = useCallback(() => {
    writeStored('granted')
    setChoice('granted')
    setIsOpen(false)
  }, [])

  const decline = useCallback(() => {
    writeStored('denied')
    setChoice('denied')
    setIsOpen(false)
    // Если согласие отзывают после того, как счётчик уже отработал
    clearMetrikaCookies()
  }, [])

  /** Повторно открыть баннер — ссылка «Настройки cookie» в подвале */
  const reopen = useCallback(() => setIsOpen(true), [])

  return { choice, isOpen, accept, decline, reopen } as const
}
